const moment = require("moment");
import classNames from "classnames";
import {
    IGoalDatapointLatest,
    resourceNameGoalDatapointsLatest,
} from "motw_core/DTOs/goaldatapointlatest";
import { IGoal } from "motw_core/Entity/IGoal";
import { GetDatapointsForGoalsContainer } from "motw_core/Repositories/DatapointRepository";
import * as React from "react";
import { DailyGoals } from "source/app/presentation/MainPanel/Goals/DailyGoals/DailyGoals";
import GoalCards from "source/app/presentation/MainPanel/Goals/GoalCards/GoalCards";
import { ClickGoal } from "source/app/presentation/MainPanel/Goals/GoalCards/Helpers/ClickGoal";
import * as styles from "source/app/presentation/MainPanel/Goals/Goals.css";
import {
    GoalsFloatingTools,
    IGoalsFloatingToolsInputs,
} from "source/app/presentation/MainPanel/Goals/GoalsFloatingTools/GoalsFloatingTools";
import { GetProcessedGoals } from "source/app/presentation/MainPanel/Goals/GetProcessedGoals";
import { IGoalsContainerState } from "source/app/presentation/MainPanel/Goals/IGoalsState";
import { MoreGoalTools } from "source/app/presentation/MainPanel/Goals/MoreGoalTools/MoreGoalTools";
import { EBadgeColor } from "source/app/presentation/Shared/ActivePanel/NavigationMenu/NavigationMenuItem/NavigationMenuItemBadge/EBadgeColor/EBadgeColor";
import {
    ClearNavigationBadge,
    SetNavigationMenuItemBadge,
} from "source/app/presentation/Shared/ActivePanel/NavigationMenu/NavigationMenuItem/NavigationMenuItemBadge/NavigationMenuItemBadge";
import { FloatingTools } from "source/app/presentation/Shared/FloatingTools/FloatingTools";
import Data from "source/app/services/Data";
import { EEventType } from "source/app/services/Events/EEventType";
import { DispatchEvent } from "source/app/services/Events/Events";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import { IsGoalDaysLeftBad } from "source/app/services/Goals/GoalBadgeCalculations";
import Nameof from "source/app/services/NameOf";
import { EPanelId } from "source/app/services/Shared/EPanelId";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { State } from "source/app/services/UI/State/State";
import { resourceNameGoal } from "../../../../../../motw_core/Entity/IGoal";
import { minimumLogInputEntryTextLength } from "../Log/LogInputForm/LogInputEntryText/LogInputEntryText";

export class GoalsContainer extends React.Component<
    IGoalsContainerProps,
    IGoalsContainerState
> {
    constructor(props: IGoalsContainerProps) {
        super(props);

        this.state = {
            Goals: [],
            GoalDataPoints: [],
            GoalDatapointsLatest: [],
            GoalStreakInfo: [],
            goalDataLoadStatus: "loading",
            showArchivedGoals: false,
            ...defaultGoalFilterState,
        };
    }

    private processedGoals: IGoal[] = [];

    // Not reading from IndexedDb because re-rendering after loading data from network is too costly
    async componentDidMount() {
        try {
            const loadedData = await Promise.all([
                this.LoadGoals(),
                this.LoadGoalDatapoints(),
                this.LoadGoalDatapointsLatest(),
                this.LoadGoalStreakInfo(),
            ]);

            this.setState({
                goalDataLoadStatus: loadedData.every(Array.isArray)
                    ? "loaded"
                    : "error",
            });
        } catch {
            this.setState({ goalDataLoadStatus: "error" });
        }

        State.ShareClass(ESharedComponent.GoalsContainer, this);
    }

    shouldComponentUpdate(
        nextProps: IGoalsContainerProps,
        nextState: IGoalsContainerState
    ) {
        if (!nextState || !Array.isArray(nextState.GoalDataPoints)) {
            return false;
        }

        const isSearchTextNotLongEnough =
            nextProps.searchText.length > 0 &&
            nextProps.searchText.length < minimumLogInputEntryTextLength;

        if (isSearchTextNotLongEnough) {
            return false;
        }

        const isSingleGoalSelected =
            nextProps.clickedGoalId !== undefined &&
            nextProps.clickedGoalId === this.props.clickedGoalId;

        if (isSingleGoalSelected) {
            return false;
        }

        this.processedGoals = GetProcessedGoals(
            nextState.Goals,
            nextState.GoalDataPoints,
            nextProps.searchText,
            nextState.selectedDirection,
            nextState.showHiddenGoals,
            nextState.showArchivedGoals,
            nextState.GoalStreakInfo,
            nextProps.clickedGoalId
        );

        return true;
    }

    componentDidUpdate() {
        UpdateNavigationLinkBadge(this.processedGoals);

        ActivateSingleGoalCardSearchResult(
            this.processedGoals,
            this.props.searchText
        );

        State.ShareClass(ESharedComponent.GoalsContainer, this);
        DispatchEvent(EEventType.GoalsContainer_DataChanged);
    }

    render() {
        return (
            <div
                id="pnlGoals"
                className={classNames(
                    "marginTopHalfem",
                    styles.panelTopGap,
                    styles.centerText,
                    styles.displayBlock
                )}
            >
                {FloatingTools<IGoalsFloatingToolsInputs>(
                    { goals: this },
                    GoalsFloatingTools.GetAll()
                )}
                <GoalCards
                    searchText={this.props.searchText}
                    goalDatapointsLatest={this.state.GoalDatapointsLatest}
                    goalsContainer={this}
                    processedGoals={this.processedGoals}
                    goalStreakInfos={this.state.GoalStreakInfo}
                />
                <DailyGoals
                    GoalsContainer={this}
                    GoalDatapointsLatest={this.state.GoalDatapointsLatest}
                    GoalStreakInfos={this.state.GoalStreakInfo}
                />
                <MoreGoalTools goalsContainer={this} />
            </div>
        );
    }

    private LoadGoals() {
        return Data.Load.Component({
            component: this,
            load: () =>
                ClientDataProvider.GetRecordsAsync<IGoal>(resourceNameGoal),
            stateTarget: Nameof<IGoalsContainerState>("Goals"),
            inLoadAll: true,
        });
    }

    private LoadGoalDatapoints() {
        return Data.Load.Component({
            component: this,
            load: () => GetDatapointsForGoalsContainer(ClientDataProvider),
            stateTarget: Nameof<IGoalsContainerState>("GoalDataPoints"),
            inLoadAll: true,
        });
    }

    private LoadGoalDatapointsLatest() {
        return Data.Load.Component({
            component: this,
            load: () =>
                ClientDataProvider.GetRecordsAsync<IGoalDatapointLatest>(
                    resourceNameGoalDatapointsLatest
                ),
            stateTarget: Nameof<IGoalsContainerState>("GoalDatapointsLatest"),
            inLoadAll: true,
        });
    }

    private LoadGoalStreakInfo() {
        return Data.Load.Component({
            component: this,
            load: () => MotwLogicServer.GoalStreakInfo(),
            stateTarget: Nameof<IGoalsContainerState>("GoalStreakInfo"),
            inLoadAll: true,
        });
    }
}

export default GoalsContainer;

export const defaultGoalFilterState = {
    selectedDirection: "All",
    showHiddenGoals: false,
};

interface IGoalsContainerProps {
    searchText: string;
    clickedGoalId?: number;
}

function UpdateNavigationLinkBadge(processedGoals: IGoal[]) {
    const badGoals: number = processedGoals.filter((elem) =>
        IsGoalDaysLeftBad(elem.daysleft)
    ).length;

    if (badGoals > 0) {
        SetNavigationMenuItemBadge(
            EPanelId.Habits,
            badGoals,
            EBadgeColor.darkred
        );
        return;
    }

    ClearNavigationBadge(EPanelId.Habits);
}

function ActivateSingleGoalCardSearchResult(
    processedGoals: IGoal[],
    searchText: string
) {
    if (processedGoals.length === 0) {
        return;
    }

    const userIsSearchingForGoal = searchText.length >= 3;
    const onlyOneSearchResult = processedGoals.length == 1;
    const firstSearchResult = processedGoals[0];

    if (userIsSearchingForGoal && onlyOneSearchResult) {
        ClickGoal(firstSearchResult);
    }
}
